import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/base/ArticleCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { getFeaturedArticles, articles } from '@/mocks/articles';
import { featuredBrands } from '@/mocks/brands';
import { useState } from 'react';

const heroBgImage =
  'https://readdy.ai/api/search-image?query=Elegant%20European%20cityscape%20with%20classic%20architecture%20at%20golden%20hour%2C%20warm%20terracotta%20and%20cream%20tones%2C%20soft%20atmospheric%20light%2C%20timeless%20cosmopolitan%20aesthetic%2C%20artistic%20photography%20with%20gentle%20bokeh%20and%20dreamy%20mood&width=1800&height=1000&seq=hero-bg-home-01&orientation=landscape';

const faqs = [
  {
    question: '欧美专区提供哪些内容？',
    answer: '欧美专区为您提供欧美时尚、欧美音乐、欧美影视、欧美品牌、欧美文化和欧美生活方式六大板块的精选内容，涵盖最新资讯、深度解析和趋势报道。',
  },
  {
    question: '欧美专区的内容更新频率如何？',
    answer: '我们每日更新精选欧美内容，确保您能第一时间获取欧美文化、时尚、品牌等领域的最新动态和深度报道。',
  },
  {
    question: '如何订阅欧美专区的内容更新？',
    answer: '您可以在页面底部的Newsletter订阅区域输入邮箱地址，即可免费订阅欧美专区的精选内容推送，每周为您精选最值得阅读的欧美资讯。',
  },
  {
    question: '欧美专区的内容来源是什么？',
    answer: '欧美专区的内容由专业编辑团队精心策划和撰写，部分内容参考欧美主流媒体和权威来源，确保信息的准确性和深度。',
  },
];

export default function Home() {
  const featuredArticles = getFeaturedArticles();
  const latestArticles = articles.slice(0, 6);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [email, setEmail] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setEmail('');
      return;
    }
    try {
      const body = new URLSearchParams();
      body.append('email', (formData.get('email') as string || '').trim());
      const res = await fetch('https://readdy.ai/api/form/d9sq01llbrt1qcgaj800', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setEmail('');
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || '提交失败，请稍后再试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后再试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="WebPage" data={{ name: '欧美专区首页', url: 'https://www.oumeizone.com/' }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <header className="relative w-full h-[520px] md:h-[650px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroBgImage}
              alt="欧美专区 - 精选欧美内容一站汇聚"
              title="欧美专区 · 精选欧美内容一站汇聚"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/30"></div>
          <div className="relative z-10 w-full text-center px-4 md:px-6">
            <p className="font-label text-sm md:text-base tracking-widest text-background-50/80 mb-4 uppercase">
              Europe &amp; America Zone
            </p>
            <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-6 leading-tight">
              精选欧美内容<br className="md:hidden" />一站汇聚
            </h2>
            <p className="text-background-200 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
              欧美专区为您汇聚最全面的欧美精选内容，涵盖欧美文化、欧美品牌、欧美资讯与欧美生活方式。无论您追求欧美时尚、欧美音乐还是欧美影视，欧美专区都是您最权威的一站式目的地。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/fashion"
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full font-label text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                立即探索
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3 bg-background-50/20 text-background-50 backdrop-blur-sm rounded-full font-label text-sm font-medium hover:bg-background-50/30 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </header>

        <main>
          {/* Category Grid */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                探索欧美专区
              </h2>
              <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
                六大精选分类，全方位覆盖欧美文化生活的每一个精彩维度
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group relative rounded-lg overflow-hidden h-56 cursor-pointer"
                >
                  <img
                    src={cat.image}
                    alt={cat.name}
                    title={`${cat.name} - 欧美专区`}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 flex items-center justify-center">
                        <i className={`${cat.icon} text-lg text-background-50`}></i>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-background-50">
                        {cat.name}
                      </h3>
                    </div>
                    <p className="text-sm text-background-200 line-clamp-2">{cat.description}</p>
                    <p className="text-xs text-background-400 mt-2">{cat.count} 篇文章</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          {/* Featured Articles */}
          <section className="bg-background-100 py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-10">
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    精选推荐
                  </h2>
                  <p className="text-foreground-500 text-sm mt-2">编辑精选的欧美内容，不容错过</p>
                </div>
                <Link
                  to="/category/fashion"
                  className="hidden sm:inline-flex items-center gap-1.5 text-sm font-label text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  查看全部
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                {featuredArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} />
                ))}
              </div>
            </div>
          </section>

          {/* Latest Articles Grid */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                  最新文章
                </h2>
                <p className="text-foreground-500 text-sm mt-2">最新发布的欧美精选内容</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </section>

          {/* Featured Brands */}
          <section className="bg-background-100 py-16 md:py-20">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                  精选欧美品牌
                </h2>
                <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">
                  深入解析全球最具影响力的欧美品牌故事与策略
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {featuredBrands.map((brand) => (
                  <Link
                    key={brand.name}
                    to="/category/brand"
                    className="group bg-background-50 rounded-lg p-5 text-center border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-16 h-10 mx-auto mb-3 overflow-hidden rounded">
                      <img
                        src={brand.logo}
                        alt={brand.name}
                        title={`${brand.name} - 欧美品牌推荐`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <h3 className="font-label text-sm font-semibold text-foreground-900 mb-1">
                      {brand.name}
                    </h3>
                    <p className="text-xs text-foreground-400 line-clamp-2 mb-2">
                      {brand.description}
                    </p>
                    <span className="text-xs text-primary-500 font-label">
                      {brand.articleCount} 篇文章
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { value: '536', label: '精选文章', icon: 'ri-article-line', color: 'primary' },
                { value: '128', label: '品牌解读', icon: 'ri-store-2-line', color: 'accent' },
                { value: '89K', label: '月活读者', icon: 'ri-user-heart-line', color: 'primary' },
                { value: '12', label: '编辑团队', icon: 'ri-team-line', color: 'accent' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className={`w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-${stat.color}-100`}>
                    <i className={`${stat.icon} text-xl text-${stat.color}-600`}></i>
                  </div>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-1">
                    {stat.value}
                  </div>
                  <p className="text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Newsletter */}
          <section className="relative bg-background-900 py-16 md:py-20 overflow-hidden">
            <div className="absolute inset-0 opacity-10">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20warm%20toned%20texture%20background%20with%20soft%20geometric%20patterns%2C%20elegant%20marbled%20effect%2C%20terracotta%20and%20cream%20tones%2C%20subtle%20luxurious%20atmosphere&width=1800&height=600&seq=newsletter-bg-01&orientation=landscape"
                alt=""
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="relative z-10 max-w-2xl mx-auto px-4 md:px-6 text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
                订阅欧美专区
              </h2>
              <p className="text-background-300 text-sm md:text-base mb-8">
                每周精选欧美文化、时尚、品牌资讯，直接发送到您的邮箱
              </p>
              {formStatus === 'success' ? (
                <div className="bg-accent-500/20 border border-accent-500/40 rounded-lg p-4 text-background-50">
                  <i className="ri-check-line mr-2"></i>
                  订阅成功！感谢您的关注，我们将每周为您推送精选欧美内容。
                </div>
              ) : (
                <form
                  onSubmit={handleNewsletterSubmit}
                  data-readdy-form
                  className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
                >
                  <div className="visually-hidden-field">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="输入您的邮箱地址"
                    className="flex-1 px-4 py-3 rounded-full bg-background-700 text-background-50 placeholder:text-background-400 text-sm border border-background-600 focus:outline-none focus:border-primary-400 transition-colors"
                  />
                  <button
                    type="submit"
                    className="px-6 py-3 bg-primary-500 text-background-50 rounded-full font-label text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    立即订阅
                  </button>
                </form>
              )}
              {formStatus === 'error' && (
                <p className="text-red-400 text-sm mt-3">{formError}</p>
              )}
              <p className="text-background-500 text-xs mt-4">
                我们尊重您的隐私，不会发送垃圾邮件，您可以随时取消订阅。
              </p>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="max-w-3xl mx-auto px-4 md:px-6 py-16 md:py-20">
            <div className="text-center mb-10">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-500 text-sm">关于欧美专区的常见疑问解答</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100 transition-colors">
                    <h4 className="font-label text-sm font-medium text-foreground-900 pr-4">
                      {faq.question}
                    </h4>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}